import type {
  EstimateManagerCoupon,
  EstimateManagerProductResponse,
} from '../../../core/models/estimates-orchestrator';
import type { EstimatePricing } from '../../../core/models/estimate-rating';
import type { SelectedDeliveryService } from '../../service-step/sections/delivery-service-selection/delivery-service-selection.model';

/** Coupon percent and type metadata for Review & Pay estimate breakdown labels. */
export interface ReviewCouponDiscountMetadata {
  discountRate?: number;
  discountType: string;
}

/**
 * Derives coupon discount metadata for Review & Pay, preferring API `discountRate`
 * over legacy `contractValue` and effective total savings.
 */
export function deriveReviewCouponDiscount(
  coupon: EstimateManagerCoupon | undefined,
  pricing: EstimatePricing,
  discountedPricing: EstimatePricing,
): ReviewCouponDiscountMetadata {
  const fromCouponRate =
    typeof coupon?.discountRate === 'number' &&
    Number.isFinite(coupon.discountRate) &&
    coupon.discountRate > 0
      ? Math.round(coupon.discountRate)
      : undefined;
  const fromContract = Number.parseFloat(coupon?.contractValue ?? '');
  const full = Number.parseFloat(pricing.totalPrice);
  const discounted = Number.parseFloat(discountedPricing.totalPrice);
  const fromPrices =
    Number.isFinite(full) && Number.isFinite(discounted) && full > 0 && discounted < full
      ? Math.round((1 - discounted / full) * 100)
      : undefined;
  const discountRate =
    fromCouponRate ??
    (Number.isFinite(fromContract) && fromContract > 0 ? Math.round(fromContract) : undefined) ??
    fromPrices;
  const discountType =
    typeof coupon?.discountType === 'string' && coupon.discountType.trim()
      ? coupon.discountType.trim()
      : '%';

  return { discountRate, discountType };
}

/**
 * Returns true when the discount orchestrator response cannot be applied to the flow.
 */
export function isReviewCouponDiscountRejected(input: {
  couponFailed: boolean;
  couponStatus?: EstimateManagerCoupon['status'];
  pricing: EstimatePricing | null | undefined;
  discountedPricing: EstimatePricing | null | undefined;
  estimateAttestation?: string | null;
}): boolean {
  return (
    input.couponFailed ||
    input.couponStatus !== 'VALID' ||
    !input.pricing ||
    !input.discountedPricing ||
    !input.estimateAttestation?.trim()
  );
}

/**
 * Builds the refreshed selected service after a successful coupon discount response.
 */
export function buildSelectedServiceAfterCouponDiscount(
  selectedService: SelectedDeliveryService,
  product: EstimateManagerProductResponse,
  pricing: EstimatePricing,
  discountedPricing: EstimatePricing,
  estimateAttestation: string,
  discount: ReviewCouponDiscountMetadata,
): SelectedDeliveryService {
  const shipmentDate =
    product.serviceAvailability?.shipmentDate?.trim().replaceAll('/', '-') ||
    selectedService.shipmentDate;

  return {
    ...selectedService,
    ...(shipmentDate ? { shipmentDate } : {}),
    deliveryDate: product.serviceAvailability?.expectedDeliveryDate ?? selectedService.deliveryDate,
    transportationMode:
      product.serviceAvailability?.transportationMode || selectedService.transportationMode,
    pricing,
    discountedPricing,
    ...(discount.discountRate !== undefined
      ? {
          couponDiscount: {
            discountRate: discount.discountRate,
            discountType: discount.discountType,
          },
        }
      : { couponDiscount: undefined }),
    attestation: estimateAttestation,
  };
}
